import Cookie from "js-cookie";
import CryptoJS from "crypto-js";
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from "../../../../modules/auth";
import { useLazyReadMultipleQuery } from "../../../services/data/tableDataApi";
import { useEffect, useState } from "react";
import { FieldFilterType } from "../../../base/types";
import { useUpdateCookieMutation } from "./registration/cookieApi";

interface IVerificationData {
    identifier: string
    timestamp: Date
    contactId: string
}

interface IRollNumber {
    rollNo?: string;
    createdOn?: Date;
}

const useCookieStore = (cookieName: string) => {
    const { user, siteId } = useAuth();
    const [getRecords] = useLazyReadMultipleQuery();
    const [records, setRecords] = useState<IRollNumber[]>([]);
    const [updateIdentifier] = useUpdateCookieMutation();
    const rollNo = records?.[0]?.rollNo;

    useEffect(() => {
        if (!user?.contactId) return;
        getHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const getHistory = async (): Promise<IRollNumber[]> => {
        if (!user?.contactId) {
            return Promise.reject('User contact not found.');
        }

        try {
            const data = await getRecords({
                userId: user.contactId || '',
                portalId: siteId || '',
                tableId: "contact",
                fieldList: ["slcm_rollnumber"],
                filters: [{
                    group: 0,
                    filters: [
                        { field: "contactid", type: FieldFilterType.Const, value: user.contactId }
                    ]
                }]
            }).unwrap();

            const records = data.records.map<IRollNumber>(p => ({
                rollNo: p["slcm_rollnumber"]
            }));
            setRecords(records);
            return records;
        } catch (err) {
            console.error('Error fetching history:', err);
            throw err; // Rethrow or handle the error as needed
        }
    };

    const generateIdentifier = (contactId: string): string => {
        const data: IVerificationData = {
            contactId,
            identifier: uuidv4(),
            timestamp: new Date()
        };

        const jsonText = JSON.stringify(data)

        return CryptoJS.AES.encrypt(jsonText, cookieName).toString()
    };

    const createCookie = (contactId: string) => {
        const identifier = generateIdentifier(contactId)
        const deviceType = "365970002";
        const encodedDeviceId = btoa(identifier); // Encode the identifier for API
        return updateIdentifier({ rollNo: rollNo || '', deviceId: encodedDeviceId, deviceType })
            .unwrap().then(() => {
                Cookie.set(cookieName, identifier, {
                    expires: moment().add("months", 6).toDate(),
                    path: '/'
                })
            })
    };

    const getCookie = () => {
        const value = Cookie.get(cookieName);
        if (!value) return

        const bytes = CryptoJS.AES.decrypt(value, cookieName);
        var jsonText = bytes.toString(CryptoJS.enc.Utf8);
        return JSON.parse(jsonText) as IVerificationData;
    };

    const getServerIdentifier = (serverIdentifier?: string) => {
        const value = serverIdentifier;
        if (!value) return

        const bytes = CryptoJS.AES.decrypt(value, cookieName);
        var jsonText = bytes.toString(CryptoJS.enc.Utf8);
        return JSON.parse(jsonText) as IVerificationData;
    };

    const hasValidCookie = async (contactId: string, validAfterMins: number, serverIdentifier?: string): Promise<"VALID" | "CREATED" | "INVALID"> => {
        const localCookieData = getCookie()
        const serverCookieData = getServerIdentifier(serverIdentifier);
    
        // If either the local or server data is missing, return 'INVALID'
        if (!localCookieData || !serverCookieData || localCookieData.contactId !== contactId) {
            return 'INVALID';
        }
    
        // Compare local and server identifiers
        if (localCookieData.identifier !== serverCookieData.identifier) {
            return 'INVALID'; // Identifiers do not match
        }
    
        // Check if the cookie timestamp is still valid
        const creationTime = moment(localCookieData.timestamp);
        const currentTime = moment();
        const timeDifference = moment.duration(currentTime.diff(creationTime)).asMilliseconds();
    
        if (timeDifference >= validAfterMins * 60 * 1000) {
            return 'VALID';
        } else {
            return 'CREATED';
        }
    };

    const deleteCookie = () => {
        Cookie.remove(cookieName)
    }

    return { createCookie, getCookie, hasValidCookie, deleteCookie };
};
export default useCookieStore;